import { useCallback, useEffect, useState } from 'react';

import {
  BrazeContentCard,
  BrazeSdk,
  ContentCardsHolder,
} from '@/modules/shop/models/braze';

function useBraze(feedIds?: string) {
  const [brazeSdk, setBrazeSdk] = useState<BrazeSdk | undefined>(() => {
    if (typeof window != 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return window.braze as any;
    }
    return undefined;
  });

  const [contentCards, setContentCards] = useState<BrazeContentCard[]>([]);

  useEffect(() => {
    if (brazeSdk) return;
    const intervalId = setInterval(() => {
      if (typeof window.braze != 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setBrazeSdk(window.braze as any);
        clearInterval(intervalId);
      }
    }, 300);
    return () => clearInterval(intervalId);
  }, [brazeSdk, feedIds]);

  const onContentCardsUpdate = useCallback(
    ({ cards }: ContentCardsHolder) => {
      const deviceToFilter = window.innerWidth < 600 ? 'mobile' : 'desktop';
      const brazeFeedId = (feedIds ?? '').split(',');
      let filteredContentCards = cards.filter((c) => {
        const cardFeedId = (c.extras.feed_id || '').split(',');
        return brazeFeedId.some((id) => cardFeedId.includes(id));
      });
      filteredContentCards = filteredContentCards.filter((c) =>
        (c.extras.device || '').split(',').includes(deviceToFilter)
      );
      filteredContentCards = filteredContentCards.filter((c) => !c.isControl);
      setContentCards(filteredContentCards);
    },
    [feedIds]
  );

  useEffect(() => {
    if (!brazeSdk || !feedIds) return;
    let subscription: string;
    let retryTimeoutId: NodeJS.Timeout;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).failBraze = true;

    const clearBraze = () => {
      try {
        if (subscription && brazeSdk) {
          // eslint-disable-next-line no-console
          console.log('Braze-- Removing subscription');
          brazeSdk.removeSubscription(subscription);
        }
        if (retryTimeoutId) {
          clearTimeout(retryTimeoutId);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Braze-- Error removing subscription', e);
      }
    };
    const subscribeToBraze = () => {
      try {
        if (!brazeSdk || !feedIds) return;

        subscription =
          brazeSdk.subscribeToContentCardsUpdates(onContentCardsUpdate);

        const cachedCards = brazeSdk.getCachedContentCards();
        onContentCardsUpdate(cachedCards);
        brazeSdk.requestContentCardsRefresh();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Braze-- Error initializing. Retrying.....', e);
        clearBraze();
        retryTimeoutId = setTimeout(subscribeToBraze, 200);
      }
    };

    const decodeAs = (token: string) => {
      const payloadBase64 = token.split('.')[1];
      const decodedJson = Buffer.from(payloadBase64, 'base64').toString();
      const decoded = JSON.parse(decodedJson);
      return decoded;
    };

    const isJwtTokenExpiringInMinutes = (token: string, minutes: number) => {
      const decoded = decodeAs(token);
      return Date.now() + minutes * 60 * 1000 >= decoded.exp * 1000;
    };

    const isJwtTokenExpired = (token: string) => {
      return isJwtTokenExpiringInMinutes(token, 0);
    };

    const token = localStorage.getItem('token');
    const JWS_REGEX = /^[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*$/;
    const isIdentified =
      token && JWS_REGEX.test(token) && !isJwtTokenExpired(token);

    if (isIdentified) {
      subscribeToBraze();
    }

    return () => {
      clearBraze();
    };
  }, [brazeSdk, onContentCardsUpdate, feedIds]);

  return { contentCards, brazeSdk };
}

export default useBraze;
